import { createContext, useContext, ReactNode } from "react";
import {
  LocalizationProvider,
  useLocalization,
  type LocalizationContextType,
} from "./LocalizationContext";
import { ThemeProvider, useTheme, type ThemeContextType } from "./ThemeContext";
import { AppUIProvider, useAppUI, type AppUIContextType } from "./AppUIContext";

export type { Language } from "./LocalizationContext";

type AppContextType =
  LocalizationContextType &
  ThemeContextType &
  AppUIContextType;

const AppContext = createContext<AppContextType | undefined>(undefined);

function AppContextBridge({ children }: { children: ReactNode }) {
  const localization = useLocalization();
  const theme = useTheme();
  const appUI = useAppUI();

  return (
    <AppContext.Provider value={{ ...localization, ...theme, ...appUI }}>
      {children}
    </AppContext.Provider>
  );
}

export function AppProvider({ children }: { children: ReactNode }) {
  return (
    <LocalizationProvider>
      <ThemeProvider>
        <AppUIProvider>
          <AppContextBridge>{children}</AppContextBridge>
        </AppUIProvider>
      </ThemeProvider>
    </LocalizationProvider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp must be used within AppProvider");
  }
  return context;
}
